'use client';

import { useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { Badge, cn } from '@newfront-insurance/core-ui/v2';
import { DollarSign, Loader2, Shield } from 'lucide-react';

import {
  getAttachedFileSourceMetadata,
  getDataExplorerSourceMetadata,
  getSourceMetadata,
  type SourcePart,
} from '../chat-v2/lib/sources';

export type SourceBadgeProps = {
  className?: string;
  source: SourcePart;
  index: number;
  size?: 'sm' | 'default';
};

function EntityTypeIcon({ entityType }: { entityType: string }) {
  switch (entityType.toLowerCase()) {
    case 'claim':
    case 'invoice':
      return <DollarSign className="h-3 w-3" />;
    case 'policy':
    default:
      return <Shield className="h-3 w-3" />;
  }
}

export function SourceBadge({ source, index, size = 'default', className }: SourceBadgeProps) {
  const { preview, isDownloading } = useDownloadFile({});
  const dataExplorerMeta = getDataExplorerSourceMetadata(source);
  const documentSearchMeta = getSourceMetadata(source);
  const attachedFileMeta = getAttachedFileSourceMetadata(source);
  const fileUuid = documentSearchMeta?.fileUuid ?? attachedFileMeta?.fileUuid ?? undefined;
  const badge = (
    <Badge
      variant="secondary"
      className={cn(
        'hover:bg-secondary/80 cursor-pointer transition-colors',
        size === 'sm' && 'gap-1 px-2 py-0.5 text-xs font-normal',
        isDownloading && 'opacity-80',
      )}
    >
      {isDownloading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : dataExplorerMeta ? (
        <EntityTypeIcon entityType={dataExplorerMeta.entityType} />
      ) : null}
      {index + 1}. {source.title}
    </Badge>
  );

  if (fileUuid) {
    return (
      <button
        type="button"
        className={cn('no-underline disabled:cursor-progress', className)}
        disabled={isDownloading}
        aria-busy={isDownloading}
        onClick={() => void preview(fileUuid, source.title)}
      >
        {badge}
      </button>
    );
  }

  const url = 'url' in source ? source.url : undefined;

  if (!url) {
    return <span className={className}>{badge}</span>;
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={cn('no-underline', className)}>
      {badge}
    </a>
  );
}
